/** Pure layout maths for the home desk: where each card sits in "scatter" and "tidy" mode. */

export interface Size {
  w: number;
  h: number;
}

export interface Box extends Size {
  x: number;
  y: number;
}

export interface Placement {
  x: number;
  y: number;
  /** Rotation in degrees. */
  r: number;
}

export interface CardSize extends Size {
  id: string;
}

export interface DeskMeasure {
  width: number;
  photo: Size;
  /** The wide search card: placed first, just above the polaroid (scatter) or in its own top row (tidy). */
  lead: CardSize | null;
  cards: CardSize[];
}

export interface DeskLayout {
  height: number;
  /** Vertical centre of the polaroid, in px from the desk's top edge. */
  photoTop: number;
  placements: Record<string, Placement>;
}

export const BASE_DESK_HEIGHT = 760;
const EDGE = 6;
const SCATTER_SEED = 11;
const LEAD_TILT = -2;
const LEAD_GAP = 10;
const SCATTER_TRIES = 260;
const TIDY_GAP = 16;
const TIDY_COLUMN = 230;

/** Deterministic PRNG (mulberry32) so the scatter looks the same on every visit. */
function seeded(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function overlapArea(a: Box, b: Box): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}

/** The polaroid is centred horizontally and slightly rotated; the padding covers its tilted corners. */
function photoBox(deskWidth: number, photoTop: number, photo: Size, pad: number): Box {
  return {
    x: deskWidth / 2 - photo.w / 2 - pad,
    y: photoTop - photo.h / 2 - pad,
    w: photo.w + pad * 2,
    h: photo.h + pad * 2,
  };
}

function scatterHeight(m: DeskMeasure): number {
  const all = m.lead ? [m.lead, ...m.cards] : m.cards;
  const area = all.reduce((sum, c) => sum + c.w * c.h, 0);
  return Math.max(BASE_DESK_HEIGHT, Math.round((area * 2.3) / m.width + 200));
}

/** Random spot with the least overlap against what is already on the desk. */
function bestSpot(card: Size, desk: Size, blocked: Box[], rand: () => number): Box {
  const candidate = (): Box => ({
    x: 14 + rand() * (desk.w - card.w - 28),
    y: 14 + rand() * (desk.h - card.h - 28),
    w: card.w,
    h: card.h,
  });
  const cost = (box: Box) => blocked.reduce((sum, b) => sum + overlapArea(box, b), 0);
  let best = candidate();
  let bestCost = cost(best);
  for (let i = 1; i < SCATTER_TRIES && bestCost > 0; i++) {
    const box = candidate();
    const boxCost = cost(box);
    if (boxCost < bestCost) {
      best = box;
      bestCost = boxCost;
    }
  }
  return best;
}

/** Centred just above the polaroid, kept inside the desk. */
function leadAbovePhoto(lead: CardSize, photo: Box, desk: Size): Placement {
  const raw = { x: photo.x + photo.w / 2 - lead.w / 2, y: photo.y - lead.h - LEAD_GAP, r: LEAD_TILT };
  const p = clampPlacement(raw, lead, desk);
  return { ...p, x: Math.round(p.x), y: Math.round(p.y) };
}

export function scatterLayout(m: DeskMeasure): DeskLayout {
  const height = scatterHeight(m);
  const photoTop = height / 2;
  const rand = seeded(SCATTER_SEED);
  const photo = photoBox(m.width, photoTop, m.photo, 18);
  const blocked = [photo];
  const placements: Record<string, Placement> = {};
  if (m.lead) {
    const p = leadAbovePhoto(m.lead, photo, { w: m.width, h: height });
    placements[m.lead.id] = p;
    blocked.push({ x: p.x - 8, y: p.y - 8, w: m.lead.w + 16, h: m.lead.h + 16 });
  }
  for (const card of m.cards) {
    const spot = bestSpot(card, { w: m.width, h: height }, blocked, rand);
    placements[card.id] = { x: Math.round(spot.x), y: Math.round(spot.y), r: Math.round((rand() - 0.5) * 16) };
    blocked.push({ x: spot.x - 8, y: spot.y - 8, w: card.w + 16, h: card.h + 16 });
  }
  return { height, photoTop, placements };
}

function shortestColumn(columnY: number[]): number {
  return columnY.reduce((best, y, i) => (y < (columnY[best] ?? Infinity) ? i : best), 0);
}

/**
 * Masonry columns centred on the desk, below a top row for the search card;
 * a card that would cover the polaroid drops below it.
 */
export function tidyLayout(m: DeskMeasure): DeskLayout {
  const photoTop = BASE_DESK_HEIGHT / 2;
  const columns = Math.max(1, Math.floor((m.width - TIDY_GAP) / (TIDY_COLUMN + TIDY_GAP)));
  const offset = (m.width - columns * (TIDY_COLUMN + TIDY_GAP) + TIDY_GAP) / 2;
  const placements: Record<string, Placement> = {};
  let top = TIDY_GAP + 6;
  if (m.lead) {
    placements[m.lead.id] = { x: Math.round((m.width - m.lead.w) / 2), y: top, r: 0 };
    top += m.lead.h + TIDY_GAP;
  }
  const columnY = Array.from({ length: columns }, () => top);
  const photo = photoBox(m.width, photoTop, m.photo, TIDY_GAP);
  for (const card of m.cards) {
    const col = shortestColumn(columnY);
    const x = offset + col * (TIDY_COLUMN + TIDY_GAP) + (TIDY_COLUMN - card.w) / 2;
    let y = columnY[col] ?? TIDY_GAP;
    if (overlapArea({ x, y, w: card.w, h: card.h }, photo) > 0) y = photo.y + photo.h;
    placements[card.id] = { x: Math.round(x), y: Math.round(y), r: 0 };
    columnY[col] = y + card.h + TIDY_GAP;
  }
  const height = Math.max(BASE_DESK_HEIGHT, Math.max(...columnY) + TIDY_GAP);
  return { height, photoTop, placements };
}

/** Keeps a card fully inside the desk. */
export function clampPlacement(p: Placement, card: Size, desk: Size): Placement {
  return {
    ...p,
    x: Math.min(Math.max(EDGE, p.x), desk.w - card.w - EDGE),
    y: Math.min(Math.max(EDGE, p.y), desk.h - card.h - EDGE),
  };
}
